import interLatin from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2";
import * as Dialog from "@radix-ui/react-dialog";
import * as Sentry from "@sentry/browser";
import { Camera, ImagePlus, Loader2, X } from "lucide-react";
import { domToBlob } from "modern-screenshot";
// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { sentry_config } from "../globals";
import Modal from "./Modal";

const FeedbackContext = createContext<(() => void) | null>(null);

/** Opens the feedback dialog; `null` where no feedback service is configured. */
export const useShareFeedback = () => useContext(FeedbackContext);

/**
 * Hosts the feedback dialog for the whole layout. Feedback goes to Sentry, with the session
 * replay and an optional screenshot attached. The dialog is ours rather than Sentry's widget:
 * the widget injects inline styles, which the release builds' Content-Security-Policy blocks,
 * and its screen capture is refused by the desktop shell. Our screenshots render the page's
 * DOM instead, which needs no permission.
 */
export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const share = useCallback(() => setOpen(true), []);
  if (!sentry_config.use_sentry) return <>{children}</>;
  return (
    <FeedbackContext.Provider value={share}>
      {children}
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </FeedbackContext.Provider>
  );
}

const button = "rounded-xl px-4 py-2 text-sm transition";
const attachButton =
  "inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-text-mid transition hover:bg-muted-surface hover:text-text";

/**
 * The app font as a rule screenshots can use: a screenshot is an image, which can't load font
 * files, so the font is inlined. We pass the rule ourselves because the library's own font lookup
 * creates a <style> element, which our Content-Security-Policy blocks, and then never settles.
 * Latin only, to keep captures light; other scripts fall back to the system font.
 */
const fontCss = async () => {
  const font = await (await fetch(interLatin)).blob();
  const url = await new Promise<string>(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(font);
  });
  return `@font-face { font-family: "Inter Variable"; font-weight: 100 900; src: url(${url}); }`;
};

/** Renders the page as the user sees it, inner scroll positions included, without `leaveOut`. */
const captureScreen = async (leaveOut: Node | null) =>
  domToBlob(document.body, {
    scale: window.devicePixelRatio,
    features: { restoreScrollPosition: true },
    font: { cssText: await fontCss() },
    filter: node => node !== leaveOut,
  });

const hasFiles = (event: React.DragEvent) => event.dataTransfer.types.includes("Files");

function FeedbackDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  // An unsent draft survives closing the dialog; a sent one is cleared.
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!screenshot) return setPreview(null);
    const url = URL.createObjectURL(screenshot);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);
  /** Takes the first image among `files`, if any. */
  const attach = (files: FileList | null) => {
    const image = Array.from(files ?? []).find(file => file.type.startsWith("image/"));
    if (image) setScreenshot(image);
    return !!image;
  };
  const takeScreenshot = async () => {
    setCapturing(true);
    try {
      // The dialog stays open to show progress, and is left out of the screenshot.
      const blob = await captureScreen(layer.current);
      setScreenshot(new File([blob], "screenshot.png", { type: blob.type }));
    } finally {
      setCapturing(false);
    }
  };
  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    try {
      const attachments = screenshot
        ? [
            {
              filename: screenshot.name || "screenshot.png",
              data: new Uint8Array(await screenshot.arrayBuffer()),
              contentType: screenshot.type,
            },
          ]
        : undefined;
      Sentry.captureFeedback({ message: message.trim() }, { includeReplay: true, attachments });
      setMessage("");
      setScreenshot(null);
      setSent(true);
    } finally {
      setSending(false);
    }
  };
  return (
    // Holds the modal's layers, which render in place, so a screenshot can leave them all out.
    <div ref={layer} className="contents">
      <Modal
        open={open}
        busy={sending}
        onOpenChange={nextOpen => {
          if (!nextOpen) setSent(false);
          onOpenChange(nextOpen);
        }}
      >
        <div className="pr-10">
          <Dialog.Title className="text-lg font-semibold text-text">
            {t(sent ? "feedback.sent_title" : "feedback.title")}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-text-mid">
            {t(sent ? "feedback.sent_description" : "feedback.description")}
          </Dialog.Description>
        </div>
        {sent ? (
          <div className="mt-5 flex justify-end">
            <Dialog.Close
              className={`${button} bg-primary font-medium text-white hover:opacity-90`}
            >
              {t("close")}
            </Dialog.Close>
          </div>
        ) : (
          <form
            className="relative mt-5 space-y-3"
            onSubmit={event => void send(event)}
            onPaste={event => {
              if (attach(event.clipboardData.files)) event.preventDefault();
            }}
            onDragOver={event => {
              if (!hasFiles(event)) return;
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={event => {
              // Moving between the form's own elements also fires dragleave on it.
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setDragging(false);
              }
            }}
            onDrop={event => {
              if (!hasFiles(event)) return;
              event.preventDefault();
              setDragging(false);
              attach(event.dataTransfer.files);
            }}
          >
            <textarea
              aria-label={t("feedback.message_label")}
              value={message}
              onChange={event => setMessage(event.target.value)}
              placeholder={t("feedback.message_placeholder")}
              required
              className="block min-h-32 w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition placeholder:text-text-dim focus:border-primary"
            />
            {preview ? (
              <div className="flex items-center gap-3 rounded-xl border border-outline p-2">
                <img
                  src={preview}
                  alt={t("feedback.screenshot_alt")}
                  className="h-16 max-w-32 rounded-lg border border-outline object-cover"
                />
                <span className="min-w-0 flex-1 truncate text-sm text-text-mid">
                  {screenshot?.name}
                </span>
                <button
                  type="button"
                  aria-label={t("feedback.remove_screenshot")}
                  onClick={() => setScreenshot(null)}
                  className="rounded-lg p-2 text-text-mid transition hover:bg-muted-surface hover:text-text"
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>
            ) : (
              // Pulled left so the icons line up with the message box.
              <div className="-ml-2 flex flex-wrap gap-1">
                <button
                  type="button"
                  disabled={capturing}
                  onClick={() => void takeScreenshot()}
                  className={`${attachButton} disabled:cursor-progress`}
                >
                  {capturing ? (
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera aria-hidden="true" className="h-4 w-4" />
                  )}
                  {t(capturing ? "feedback.taking_screenshot" : "feedback.take_screenshot")}
                </button>
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className={attachButton}
                >
                  <ImagePlus aria-hidden="true" className="h-4 w-4" />
                  {t("feedback.attach_image")}
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={event => {
                    attach(event.target.files);
                    event.target.value = "";
                  }}
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Dialog.Close
                type="button"
                disabled={sending}
                className={`${button} border border-outline text-text-dim hover:text-text`}
              >
                {t("cancel")}
              </Dialog.Close>
              <button
                type="submit"
                disabled={sending || capturing || !message.trim()}
                className={`${button} bg-primary font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {t("feedback.submit")}
              </button>
            </div>
            {dragging && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl border-2 border-dashed border-info bg-surface text-sm font-medium text-info">
                {t("feedback.drop_image")}
              </div>
            )}
          </form>
        )}
      </Modal>
    </div>
  );
}
